import { Server } from "lucide-react";
import { isOvhEnabled, isSharePointEnabled, isJiraEnabled, isSpinnakerEnabled, isNotionEnabled, isCloudBeesEnabled } from "@/lib/feature-flags";
import type { ConnectorConfig } from "./types";

class ConnectorRegistry {
  private connectors: Map<string, ConnectorConfig> = new Map();

  constructor() {
    this.registerDefaultConnectors();
  }

  private registerDefaultConnectors() {
    this.register({
      id: "mindops_apm",
      name: "MindOps APM",
      description:
        "Connect MindOps APM to bring services, metrics, logs, and traces into the cross-APM canonical model.",
      iconPath: "/mindy-logo.png",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/sre/services",
      storageKey: "isMindOpsApmConnected",
      overviewPath: "/sre/services",
      overviewLabel: "Discovered Services",
    });

    this.register({
      id: "sre_datadog",
      name: "Datadog (SRE Intelligence)",
      description:
        "Bring Datadog's service catalog into the cross-APM service list so the same service can be recognised across tools.",
      iconPath: "/datadog.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/sre/services",
      storageKey: "isSreDatadogConnected",
      overviewPath: "/sre/mappings",
      overviewLabel: "Review Mappings",
    });

    this.register({
      id: "grafana",
      name: "Grafana",
      description: "Monitor your infrastructure and receive real-time alerts from Grafana dashboards. Connect your Grafana instance to get instant notifications and insights.",
      iconPath: "/grafana.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/grafana/auth",
      storageKey: "isGrafanaConnected",
      alertsPath: "/grafana/alerts",
      alertsLabel: "View Alerts",
    });

    this.register({
      id: "datadog",
      name: "Datadog",
      description: "Bring Datadog logs, metrics, and monitor alerts into Mindy SRE AI Agent. Connect with your Datadog service account to centralize observability insights.",
      iconPath: "/datadog.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/datadog/auth",
      storageKey: "isDatadogConnected",
      alertsPath: "/datadog/events",
      alertsLabel: "View Events",
      overviewPath: "/datadog/overview",
      overviewLabel: "Overview",
      useCustomConnection: true,
    });

    this.register({
      id: "netdata",
      name: "Netdata",
      description: "Real-time infrastructure monitoring with Netdata Cloud. Receive alerts and monitor system metrics across all your nodes.",
      iconPath: "/netdata.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/netdata/auth",
      storageKey: "isNetdataConnected",
      alertsPath: "/netdata/alerts",
      alertsLabel: "View Alerts",
    });

    this.register({
      id: "splunk",
      name: "Splunk",
      description: "Connect to Splunk Cloud or Enterprise for log analytics, search, and security monitoring.",
      iconPath: "/splunk.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/splunk/auth",
      storageKey: "isSplunkConnected",
      alertsPath: "/splunk/alerts",
      alertsLabel: "View Alerts",
    });

    this.register({
        id: "dynatrace",
        name: "Dynatrace",
        description: "Connect to Dynatrace for full-stack observability. Receive problem notifications and query metrics, logs, and entities for root cause analysis.",
        iconPath: "/dynatrace.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Monitoring",
        path: "/dynatrace/auth",
        storageKey: "isDynatraceConnected",
        alertsPath: "/dynatrace/alerts",
        alertsLabel: "View Problems",
      });

    this.register({
      id: "coroot",
      name: "Coroot",
      description: "Connect Coroot for full-stack observability: metrics, logs, traces, incidents, service maps, and profiling.",
      iconPath: "/coroot.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/coroot/auth",
      storageKey: "isCorootConnected",
    });

    this.register({
      id: "newrelic",
      name: "New Relic",
      description: "Full-stack observability with New Relic. Query metrics, logs, traces, and alert issues via NerdGraph for automated root cause analysis.",
      iconPath: "/newrelic.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/newrelic/auth",
      storageKey: "isNewRelicConnected",
      useCustomConnection: true,
    });

    this.register({
      id: "sentry",
      name: "Sentry",
      description: "Connect Sentry to ingest issue and error alerts and query full stacktraces and breadcrumbs for automated root cause analysis.",
      iconPath: "/sentry.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/sentry/auth",
      storageKey: "isSentryConnected",
      useCustomConnection: true,
    });

    this.register({
        id: "thousandeyes",
        name: "ThousandEyes",
        description: "Connect Cisco ThousandEyes for network intelligence: tests, alerts, path visualization, BGP monitoring, and Internet Insights outage detection.",
        iconPath: "/thousandeyes.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Monitoring",
        path: "/thousandeyes/auth",
        storageKey: "isThousandEyesConnected",
      });

    this.register({
      id: "cloudwatch",
      name: "CloudWatch",
      description: "Receive Amazon CloudWatch alarm notifications via SNS webhooks. Alarm state changes automatically create incidents and trigger root cause analysis.",
      iconPath: "/aws.ico",
      iconBgColor: "bg-white dark:bg-white",
      category: "Monitoring",
      path: "/aws/onboarding",
      storageKey: "isCloudWatchConnected",
    });

    this.register({
      id: "pagerduty",
      name: "PagerDuty",
      description: "Connect PagerDuty to receive incident alerts and manage on-call schedules. Integrate with your PagerDuty account for real-time incident management.",
      iconPath: "/pagerduty.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Incident Management",
      path: "/pagerduty/auth",
      storageKey: "isPagerDutyConnected",
    });

    this.register({
      id: "opsgenie",
      name: "OpsGenie / JSM",
      description: "Connect OpsGenie or Jira Service Management for alert tracking and on-call schedules",
      iconPath: "/opsgenie.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Incident Management",
      path: "/opsgenie/auth",
      storageKey: "isOpsGenieConnected",
    });

    this.register({
        id: "incidentio",
        name: "incident.io",
        description: "Connect incident.io for real-time incident lifecycle tracking. Receive webhook events, investigate incidents with timeline data, and post RCA results back automatically.",
        iconPath: "/incidentio.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Incident Management",
        path: "/incident-io/auth",
        storageKey: "isIncidentIoConnected",
        alertsPath: "/incident-io/incidents",
        alertsLabel: "View Incidents",
      });

    this.register({
        id: "bigpanda",
        name: "BigPanda",
        description: "Connect BigPanda for AIOps incident correlation. Receive pre-correlated incident clusters with enriched metadata for improved root cause analysis.",
        iconPath: "/bigpanda.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Incident Management",
        path: "/bigpanda/auth",
        storageKey: "isBigPandaConnected",
      });

    this.register({
      id: "confluence",
      name: "Confluence",
      description: "Fetch runbooks and documentation from Confluence pages to automate incident response workflows.",
      iconPath: "/confluence.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Documentation",
      path: "/confluence/connect",
      storageKey: "isConfluenceConnected",
    });

    if (isJiraEnabled()) {
      this.register({
        id: "jira",
        name: "Jira",
        description: "Search issues, track incidents, and export postmortem action items as tracked Jira work.",
        iconPath: "/jira.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Documentation",
        path: "/jira/connect",
        storageKey: "isJiraConnected",
      });
    }

    if (isSharePointEnabled()) {
      this.register({
        id: "sharepoint",
        name: "SharePoint",
        description: "Fetch documents, site pages, and search across SharePoint Online sites to automate incident response workflows.",
        iconPath: "/sharepoint.png",
        iconBgColor: "bg-white dark:bg-white",
        category: "Documentation",
        path: "/sharepoint/connect",
        storageKey: "isSharePointConnected",
      });
    }

    if (isNotionEnabled()) {
      this.register({
        id: "notion",
        name: "Notion",
        description: "Export postmortems, search workspace docs, and let Mindy SRE AI Agent create runbooks and action-item rows in your Notion workspace.",
        iconPath: "/notion.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Documentation",
        path: "/notion/connect",
        storageKey: "isNotionConnected",
      });
    }

    this.register({
      id: "kubectl",
      name: "Kubernetes",
      description: "Deploy the Mindy SRE AI Agent agent into your Kubernetes cluster so Mindy SRE AI Agent can perform root cause analysis investigations securely.",
      iconPath: "/kubernetes-svgrepo-com.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Infrastructure",
      path: "/kubectl/manage",
      storageKey: "isKubectlConnected",
    });

    this.register({
      id: "github",
      name: "GitHub",
      description: "Integrate with GitHub to manage repositories, track issues, and automate workflows. Connect your GitHub account to enable seamless code collaboration.",
      iconPath: "/github-mark.svg",
      iconClassName: "dark:invert",
      iconBgColor: "bg-gray-200 dark:bg-gray-800",
      category: "Development",
      useCustomConnection: true,
    });

    this.register({
      id: "gitlab",
      name: "GitLab",
      description: "Connect to GitLab to investigate code changes, pipelines, merge requests, and manage fixes during incident response.",
      iconPath: "/gitlab.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Development",
      useCustomConnection: true,
    });

    this.register({
        id: "bitbucket",
        name: "Bitbucket",
        description: "Connect to Bitbucket Cloud to browse workspaces, manage repositories, track pull requests, and collaborate on code.",
        iconPath: "/bitbucket.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Development",
        useCustomConnection: true,
        storageKey: "isBitbucketConnected",
      });

    this.register({
        id: "slack",
        name: "Slack",
        description: "Receive alerts and notifications directly in your Slack workspace. Connect your Slack workspace to get real-time updates and interact with Mindy SRE AI Agent.",
        iconPath: "/slack.png",
        iconBgColor: "bg-white dark:bg-white",
        category: "Communication",
        storageKey: "isSlackConnected",
        useCustomConnection: true,
      });

    this.register({
        id: "google_chat",
        name: "Google Chat",
        description: "Receive alerts and notifications directly in Google Chat. Connect your Google Workspace to get real-time updates and interact with Mindy SRE AI Agent.",
        iconPath: "/google-chat.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Communication",
        storageKey: "isGoogleChatConnected",
        useCustomConnection: true,
      });

    // Cloud Providers (now under Infrastructure category)
    this.register({
      id: "gcp",
      name: "Google Cloud",
      description: "Connect to Google Cloud Platform to manage cloud resources, monitor services, and deploy applications across your GCP infrastructure.",
      iconPath: "/google-cloud-svgrepo-com.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Infrastructure",
      storageKey: "isGCPConnected",
      useCustomConnection: true,
    });

    this.register({
      id: "azure",
      name: "Azure",
      description: "Connect to Microsoft Azure to manage cloud resources, monitor services, and deploy applications across your Azure infrastructure.",
      iconPath: "/azure.ico",
      iconBgColor: "bg-muted",
      category: "Infrastructure",
      path: "/azure/auth",
      storageKey: "isAzureConnected",
      useCustomConnection: true,
    });

    this.register({
      id: "aws",
      name: "AWS",
      description: "Connect to Amazon Web Services to manage cloud resources, monitor services, and deploy applications across your AWS infrastructure.",
      iconPath: "/aws.ico",
      iconBgColor: "bg-white dark:bg-white",
      category: "Infrastructure",
      path: "/aws/onboarding",
      storageKey: "isAWSConnected",
      useCustomConnection: true,
    });

    if (isOvhEnabled()) {
      this.register({
        id: "ovh",
        name: "OVH Cloud",
        description: "Connect to OVH Cloud to manage your Public Cloud projects, monitor resources, and deploy applications across OVH infrastructure.",
        iconPath: "/ovh.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "Infrastructure",
        path: "/ovh/onboarding",
        storageKey: "isOVHConnected",
        useCustomConnection: true,
      });
    }

    this.register({
      id: "scaleway",
      name: "Scaleway",
      description: "Connect to Scaleway to manage cloud resources, monitor services, and deploy applications across your Scaleway infrastructure.",
      iconPath: "/scaleway.svg",
      iconBgColor: "bg-muted",
      category: "Infrastructure",
      path: "/scaleway/onboarding",
      storageKey: "isScalewayConnected",
      useCustomConnection: true,
    });

    this.register({
      id: "tailscale",
      name: "Tailscale",
      description: "Connect to Tailscale to manage your private network, access resources securely, and configure networking across your infrastructure.",
      iconPath: "/tailscale.svg",
      iconBgColor: "bg-muted",
      category: "Networking",
      path: "/tailscale/onboarding",
      storageKey: "isTailscaleConnected",
    });

    this.register({
      id: "cloudflare",
      name: "Cloudflare",
      description: "Connect to Cloudflare for DNS management, cache purging, WAF & firewall rules, traffic analytics, Workers monitoring, and load balancer control.",
      iconPath: "/cloudflare.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Infrastructure",
      path: "/cloudflare/auth",
      storageKey: "isCloudflareConnected",
    });

    this.register({
      id: "flyio",
      name: "Fly.io",
      description: "Connect to Fly.io for application monitoring, machine lifecycle management, metrics, logs, and incident remediation across your org.",
      iconPath: "/flyio.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "Infrastructure",
      path: "/flyio/auth",
      storageKey: "isFlyioConnected",
    });

    this.register({
      id: "onprem",
      name: "Instances SSH Access",
      description: "Manage SSH keys and configure virtual machines for on-premises and cloud infrastructure access.",
      icon: Server,
      iconColor: "text-foreground",
      iconBgColor: "bg-muted",
      category: "Infrastructure",
      path: "/vm-config",
      storageKey: "isOnPremConnected",
    });

    this.register({
      id: "jenkins",
      name: "Jenkins",
      description: "Connect to Jenkins to view jobs, builds, pipeline status, and build agents. Read-only access to your CI/CD server.",
      iconPath: "/jenkins.svg",
      iconBgColor: "bg-white dark:bg-white",
      category: "CI/CD",
      path: "/jenkins/auth",
      storageKey: "isJenkinsConnected",
    });

    if (isCloudBeesEnabled()) {
      this.register({
        id: "cloudbees",
        name: "CloudBees CI",
        description: "Connect to CloudBees CI to view jobs, builds, pipeline status, and build agents. Enterprise Jenkins with Operations Center support.",
        iconPath: "/cloudbees.svg",
        iconBgColor: "bg-muted",
        category: "CI/CD",
        path: "/cloudbees/auth",
        storageKey: "isCloudBeesConnected",
      });
    }

    if (isSpinnakerEnabled()) {
      this.register({
        id: "spinnaker",
        name: "Spinnaker",
        description: "Connect to Spinnaker for deployment pipeline visibility, application health monitoring, and automated incident correlation with CD events.",
        iconPath: "/spinnaker.svg",
        iconBgColor: "bg-white dark:bg-white",
        category: "CI/CD",
        path: "/spinnaker/auth",
        storageKey: "isSpinnakerConnected",
      });
    }
  }

  register(connector: ConnectorConfig): void {
    this.connectors.set(connector.id, connector);
  }

  get(id: string): ConnectorConfig | undefined {
    return this.connectors.get(id);
  }

  getAll(): ConnectorConfig[] {
    return Array.from(this.connectors.values());
  }

  getByCategory(category: string): ConnectorConfig[] {
    return this.getAll().filter(c => c.category === category);
  }
}

export const connectorRegistry = new ConnectorRegistry();
